import React, { useEffect, useState } from "react";
import { IoMoon, IoSunny } from "react-icons/io5";
import { StatsCard } from "./components/StatsCard";
import { calcMetadata, Metadata } from "./helpers/metadata";
import { debounce } from "./helpers/debounce";

const MAX_INPUT_LENGTH = 100; // Maximum input length

function App() {
  const [dark, setDark] = React.useState(false);

  const darkModeHandler = () => {
    setDark(!dark);
    document.body.classList.toggle("dark");
  };

  const [enableCharacterLimit, setEnableCharacterLimit] = useState(false);
  const [excludeSpaces, setExcludeSpaces] = useState(false);


  const [inputValue, setInputValue] = useState<string>("");
  const [metadata, setMetadata] = useState<Metadata>({
    characterCount: 0,
    wordCount: 0,
    sentenceCount: 0,
    readingTime: "0 minutes",
    charFrequency: null,
  });

  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  useEffect(() => {
    if (inputValue.length === 0) {
      setMetadata({
        characterCount: 0,
        wordCount: 0,
        sentenceCount: 0,
        readingTime: "0 minutes",
        charFrequency: null,
      });
      return;
    }
    if (inputValue.length > MAX_INPUT_LENGTH && enableCharacterLimit) {
      setErrorMessage(`Input exceeds ${MAX_INPUT_LENGTH} characters`);
      return;
    }
    setErrorMessage("");
    setMetadata(calcMetadata(inputValue, excludeSpaces));
  }, [inputValue, enableCharacterLimit, excludeSpaces]);

  return (
    <div className="dark:bg-[#13131d] bg-slate-100 min-h-screen h-full w-full dark:text-white text-black pt-5 pb-10 overscroll-none">
      <div className="h-full mx-80">
        <div className="h-12 w-full px-3 flex flex-row justify-between">
          <div className="h-full flex items-center">
            <img
              src="logo.svg"
              alt="here should be the logo"
              className="w-10"
            />
            <p className="text-2xl font-bold pl-3">Character Counter</p>
          </div>
          <div className="w-12 h-full flex items-center justify-center dark:bg-[#3b3a3d] bg-slate-300 rounded-full">
            <button onClick={darkModeHandler}>
              {dark ? (
                <IoSunny className="text-2xl" />
              ) : (
                <IoMoon className="text-2xl" />
              )}
            </button>
          </div>
        </div>
        <h1 className="text-3xl font-bold text-center my-10">
          Analyse your text in real-time.
        </h1>
        {errorMessage && (
          <div className="bg-red-500 text-white p-2 pl-6 rounded-md mb-4">
            {errorMessage}
          </div>
        )}
        <textarea
          onFocus={(e) => e.target.style.outline = "none"}
          onChange={debounce(handleChange, 200)}
          className={`dark:bg-[#22222c] bg-slate-300 w-full min-h-56 dark:text-slate-300 text-slate-800 resize-none rounded-xl p-3 pl-5 ${errorMessage ? "border-2 border-red-500" : "border-none"
            }`}
          placeholder="Start typing here... (or paste your text)"
        />
        <div className="h-10 px-3 flex flex-row align-self-bottom items-center justify-between">
          <div>
            <label className="px-3">
              <input
                type="checkbox"
                defaultChecked={excludeSpaces}
                onChange={(e) => setExcludeSpaces(e.target.checked)}
              />
              <span className="pl-1">Exclude Spaces</span>
            </label>
            <label className="px-3">
              <input
                type="checkbox"
                defaultChecked={enableCharacterLimit}
                onChange={(e) => setEnableCharacterLimit(e.target.checked)}
              />
              <span className="pl-1">Set Character Limit</span>
            </label>
          </div>
          <h4 className="justify-self-end">
            Approximate reading time {metadata.readingTime}
          </h4>
        </div>
        <div className="h-32 w-full mt-5 flex flex-row gap-3">
          <StatsCard
            title="Total Characters"
            value={metadata.characterCount}
            bgColor="bg-[#d3a1fa]"
          />
          <StatsCard
            title="Word Count"
            value={metadata.wordCount}
            bgColor="bg-[#ffa000]"
          />
          <StatsCard
            title="Sentence Count"
            value={metadata.sentenceCount}
            bgColor="bg-[#ff8159]"
          />
        </div>
        <div>
          <h2 className="text-2xl font-bold mt-10 pl-5">Letter Density</h2>

          {!metadata.charFrequency ? (
            <p className="text-md pl-5">See your Letter Density here.</p>
          ) : (
            <div>
              {Object.entries(metadata.charFrequency)
                .filter(([char]) => char !== " " && char !== "\n")
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([char, freq]) => (
                  <div
                    className="flex flex-row justify-between items-center py-1"
                    key={char}
                  >
                    <p className="text-md">{char}</p>
                    <div className="bg-gray-300 h-2 w-full mx-3 rounded-full">
                      <div
                        className="bg-blue-500 h-full rounded-full"
                        style={{
                          width: `${(freq / metadata.characterCount) * 100}%`,
                        }}
                      ></div>
                    </div>

                    <p className="text-md text-nowrap">
                      {freq} ({Math.round((freq / metadata.characterCount) * 100)}
                      %)
                    </p>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
